<template>
	<fieldset ref="addressFields" data-testid="address">
		<legend class="heavy sz-2 my-3">Billing Address</legend>
		<CValidatedInput id="address-line-1" name="Line 1" value="" rules="required" class="mb-2">
			<template #input="{ errors }">
				<Autocomplete
					id="address-line-1"
					:value="internalLine1"
					:class="{ 'input--error': errors[0] }"
					:popper-class="`address-automcomplete__popper`"
					:placeholder="'Address Line 1'"
					:fetch-suggestions="fetchAutocompletePredictions"
					tag-selects-suggestion
					@input="internalLine1 = $event"
					@change="handleFieldChange('Line1', $event)"
					@select="handleAutocompleteSelect"
				/>
			</template>
		</CValidatedInput>
		<CValidatedInput
			id="address-line-2"
			:value="address.Line2"
			name="Line 2"
			placeholder="Address Line 2"
			type="text"
			rules=""
			class="mb-2"
			@change="handleFieldChange('Line2', $event)"
			@blur="onAddressFieldBlur"
		/>
		<CValidatedInput
			id="address-city"
			:value="address.City"
			placeholder="City"
			name="City"
			type="text"
			:rules="cityRules"
			class="mb-2"
			@change="handleFieldChange('City', $event)"
			@blur="onAddressFieldBlur"
		/>
		<div class="flex gap-2">
			<CValidatedInput
				id="address-state"
				:value="address.State"
				placeholder="State"
				name="State"
				type="text"
				:rules="requireState"
				class="w-50 mb-2"
				@change="handleFieldChange('State', $event)"
				@blur="onAddressFieldBlur"
			/>
			<CValidatedInput
				id="address-postal-code"
				:value="address.PostalCode"
				placeholder="Postal Code"
				name="Postal Code"
				type="text"
				:rules="postalCodeRules"
				class="w-50 mb-2"
				@change="handleFieldChange('PostalCode', $event)"
				@blur="onAddressFieldBlur"
			/>
		</div>
		<CValidatedInput
			name="Country"
			value=""
			:rules="countryRules"
			class="mb-2"
		>
			<template #input="{ errors }">
				<CSelect
					id="address-country"
					:value="address.Country"
					:options="countries"
					:show-blank-option="false"
					placeholder="Country"
					class="c-dropdown"
					:class="{ 'input--error': errors[0] }"
					@change="handleFieldChange('CountryCode', $event)"
					@blur="onAddressFieldBlur"
				/>
			</template>
		</CValidatedInput>
	</fieldset>
</template>

<script lang="ts">
	import { defineComponent, ref, computed, nextTick, watch } from 'vue';
	import type { PropType } from 'vue';
	import CValidatedInput from 'src/components/form-controls/ValidatedInput.vue';
	import CSelect from 'src/components/form-controls/Select.vue';
	import { countries } from 'src/views/settings/LocationData';
	import { Autocomplete } from '@cognitoforms/element-ui';
	import { CognitoConfiguration } from '@cognitoforms/utils/clientside-configuration';
	import { GoogleMapsLoader } from '@cognitoforms/shared-components/src/components/address/google-maps-loader';
	import type { IGoogleMapsLoader } from '@cognitoforms/shared-components/src/components/address/google-maps-loader';
	import { getAddressFromComponents } from '@cognitoforms/shared-components/src/components/address/autocomplete-address';
	import type { IAddress } from '@cognitoforms/shared-components/src/components/address/autocomplete-address';
	import type { FetchSuggestionsCallback } from '@cognitoforms/element-ui/types/autocomplete';
	import { isFirefox } from '@cognitoforms/utils/user-agent';

	type Address = Pick<IAddress, 'Line1' | 'Line2' | 'City' | 'State' | 'PostalCode' | 'Country'>;

	export default defineComponent({
		name: 'CAddress',
		components: {
			CValidatedInput,
			CSelect,
			Autocomplete
		},
		props: {
			address: {
				type: Object as PropType<Address>, required: true
			},
			googleMapsLoader: {
				type: Object as PropType<IGoogleMapsLoader>, default: null
			},
			disableClientSideValidation: {
				type: Boolean, default: false
			}
		},
		emits: ['change', 'blur'],
		setup(props, { emit }) {
			const handleFieldChange = (field: string, value: string) => {
				if (field === 'Country') {
					const countryCode = countries.find(x => x.label === value).value;
					emit('change', 'CountryCode', countryCode);
					emit('change', 'Country', value);
				}
				else if (field === 'CountryCode') {
					const countryName = countries.find(x => x.value === value).label;
					emit('change', 'Country', countryName);
					emit('change', 'CountryCode', value);
				}
				else
					emit('change', field, value);

				// Firefox autofill can set a field's value (firing `change`) without ever focusing
				// it, so `onAddressFieldBlur` below never sees it. Fall back to `change` there.
				if (isFirefox(window.navigator))
					emit('blur');
			};

			const googleMapsLoader: IGoogleMapsLoader = props.googleMapsLoader ?? new GoogleMapsLoader(CognitoConfiguration.GoogleMapsPublicKey);
			const useAutocomplete = computed<boolean>(() => googleMapsLoader?.initialized() ?? false);
			const addressProperties: string[] = ['Line1', 'Line2', 'City', 'State', 'PostalCode', 'Country'];

			const isUS = computed(() => props.address.Country === 'United States');
			const cityRules = computed<string>(() => !isUS.value && props.disableClientSideValidation ? '' : 'required');
			const stateRules = computed<string>(() => isUS.value ? 'required' : '');
			const postalCodeRules = computed<string>(() => !isUS.value && props.disableClientSideValidation ? '' : 'required');
			const countryRules = computed<string>(() => !isUS.value && props.disableClientSideValidation ? '' : 'required');

			const handleAutocompleteError = (error: Error) => {
				googleMapsLoader?.disableLoader();
				console.log(error);
			};

			let previousAutocompleteSuggestions: google.maps.places.AutocompleteSuggestion[] = [];
			const fetchAutocompletePredictions = (input: string, cb: FetchSuggestionsCallback): void => {
				if (!input?.length || !useAutocomplete.value)
					return cb([]);

				// Prevents suggestions list from "flashing" or temporarily disappearing when typing quickly
				cb(previousAutocompleteSuggestions);

				googleMapsLoader.fetchAutocompleteSuggestions({ input })
					.then(({ suggestions }) => {
						const result = suggestions.map(place => ({ placePrediction: place.placePrediction, value: place.placePrediction.text }));
						previousAutocompleteSuggestions = result;
						cb(result);
					})
					.catch(handleAutocompleteError);
			};

			const internalLine1 = ref(props.address.Line1);
			watch(() => props.address.Line1, (newValue) => {
				internalLine1.value = newValue;
			});

			const handleAutocompleteSelect = (suggestion: google.maps.places.AutocompleteSuggestion) => {
				if (!useAutocomplete.value)
					return;

				googleMapsLoader.fetchFields({
					placeId: suggestion.placePrediction.placeId,
					fields: ['addressComponents']
				})
					.then(({ place }) => {
						const autocompletedAddress: IAddress = {
							Line1: props.address.Line1,
							Line2: props.address.Line2,
							Line3: '',
							City: props.address.City,
							State: props.address.State,
							PostalCode: props.address.PostalCode,
							Country: props.address.Country
						};
						getAddressFromComponents(autocompletedAddress, place.addressComponents, addressProperties);
						addressProperties.forEach((x) => { handleFieldChange(x, autocompletedAddress[x]); });
						emit('blur');
					})
					.catch(handleAutocompleteError);
			};

			const addressFields = ref(null);
			const onAddressFieldBlur = (e: FocusEvent) => {
				const focusedOnAddressFields = e?.relatedTarget
					&& (addressFields.value as Element)?.contains(e.relatedTarget as Element);
				if (!focusedOnAddressFields)
					nextTick().then(() => { emit('blur'); });
			};

			return {
				countries,
				internalLine1,
				// Address update handling
				handleFieldChange,
				// Address autocomplete
				useAutocomplete,
				fetchAutocompletePredictions,
				handleAutocompleteSelect,
				// Update address for tax
				addressFields,
				onAddressFieldBlur,
				requireState: stateRules,
				cityRules,
				postalCodeRules,
				countryRules
			};
		}
	});
</script>
<style lang="scss">
.address-automcomplete__popper {

	&.el-popper {
		@include dropdown-box;
	}

	.el-select-dropdown,
	.el-autocomplete-suggestion {

		&__list {
			display: flex;
			flex-direction: column;
			margin: 0;
			padding: 0;

			// Options
			li {
				padding: calc($gutter / 4) 4px;
				cursor: default;

				@include nowrap;

				&:hover,
				&.highlighted {
					background: var(--bg-selected);
				}
			}
		}

		&__empty {
			margin: 0;
			padding: calc($gutter / 4) 4px;
			opacity: .6;
			text-align: center;
		}

		&__wrap {
			max-height: calc(50vh - 20px);
		}
	}
}
</style>